import ComplianceModule from "@/store/modules/compliance";
import DeviceModule from "@/store/modules/device";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import { computed } from "vue";

export const isGermanCompliant = computed(() => {
  const groupId = POSConfigModule.groupId;
  const countryCode = POSConfigModule.getGroupCountryCode;

  // German compliance is required for all groups in Germany except group 19679
  if (countryCode === "DE" && groupId !== 19679) {
    return true;
  }
  return false;
});

/**
 * Spanish compliance is not yet to be released to production groups
 */
export const isSpanishCompliant = computed(() => {
  const groupId = POSConfigModule.groupId;
  const countryCode = POSConfigModule.getGroupCountryCode;
  const isTestGroup = groupId === 17125 && import.meta.env.VITE_ENV === "test";
  if (countryCode === "ES" && isTestGroup) {
    return true;
  }
  return false;
});

export const isCroatianCompliant = computed(() => {
  const countryCode = POSConfigModule.getGroupCountryCode;
  if (countryCode === "HR") {
    return true;
  }
  return false;
});

/**
 * French compliance is only active when VITE_IS_FRENCH_VERSION is set to "1" and the group country code is FR.
 * This environment variable should only be set in the following branches: fr-test and fr-prod.
 */
export const isFrenchCompliant = computed(() => {
  const countryCode = POSConfigModule.getGroupCountryCode;
  const isFrenchVersion = import.meta.env.VITE_IS_FRENCH_VERSION === "1";
  if (countryCode === "FR" && isFrenchVersion) {
    return true;
  }
  return false;
});

/**
 * Belgian compliance is only active when VITE_IS_BELGIAN_VERSION is set to "1" and the group country code is BE.
 * This environment variable should only be set in the Belgian builds, the same way VITE_IS_FRENCH_VERSION gates NF525.
 */
export const isBelgianCompliant = computed(() => {
  const countryCode = POSConfigModule.getGroupCountryCode;
  const isBelgianVersion = import.meta.env.VITE_IS_BELGIAN_VERSION === "1";
  if (countryCode === "BE" && isBelgianVersion) {
    return true;
  }
  return false;
});

export const isAustrianCompliant = computed(() => {
  const countryCode = POSConfigModule.getGroupCountryCode;
  if (
    countryCode === "AT" &&
    DeviceModule.hardwareLocation &&
    DeviceModule.hardwareLocation.FiskalyCashRegisterId
  ) {
    return true;
  }
  return false;
});

export const isItalianCompliant = computed(() => {
  const countryCode = POSConfigModule.getGroupCountryCode;
  if (
    countryCode === "IT" &&
    DeviceModule.hardwareLocation &&
    DeviceModule.hardwareLocation.FiskalyCashRegisterId
  ) {
    return true;
  }
  return false;
});

export const hasInvoiceEnabled = computed(() => {
  return (
    !isSpanishCompliant.value &&
    !isFrenchCompliant.value &&
    !isCroatianCompliant.value &&
    !isBelgianCompliant.value
  );
});

export const showWindowsAppVersion = computed(() => {
  return (
    !isSpanishCompliant.value &&
    !isFrenchCompliant.value &&
    !isCroatianCompliant.value
  );
});

export const canSellInCompliantCountry = computed(() => {
  if (isGermanCompliant.value) {
    return ComplianceModule.complianceStatus.packageInit;
  }
  // Belgium blocks sales when the FDM is unreachable, the same way the EFR
  // countries block on the EFR connection.
  if (isBelgianCompliant.value) {
    return (
      ComplianceModule.complianceStatus.packageInit &&
      ComplianceModule.complianceStatus.fdmConnection
    );
  }
  if (
    isSpanishCompliant.value ||
    isFrenchCompliant.value ||
    isCroatianCompliant.value
  ) {
    return (
      ComplianceModule.complianceStatus.packageInit &&
      ComplianceModule.complianceStatus.efrConnection
    );
  }
  return true;
});

export const isInCompliantCountry = computed(() => {
  return (
    isGermanCompliant.value ||
    isSpanishCompliant.value ||
    isFrenchCompliant.value ||
    isCroatianCompliant.value ||
    isBelgianCompliant.value
  );
});

export const isAllowedToBypassCompliance = computed(() => {
  // colleagues are allowed to bypass compliance checks
  return UserModule.isColleague;
});
